import { Link } from "wouter";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown, Star } from "lucide-react";
import { CallbackForm } from "./callback-form";
import { HeroBackground } from "./hero-background";

export function HomeHero() {
  const reduce = useReducedMotion();
  const item = (d: number) => ({
    initial: reduce ? false : { opacity: 0, y: 28 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay: d, ease: [0.2, 0.8, 0.2, 1] as const },
  });

  return (
    <section className="relative overflow-hidden bg-[hsl(var(--vx-ink))] text-white">
      <HeroBackground />

      <div className="relative container mx-auto max-w-7xl px-4 pt-14 pb-16 lg:pt-20 lg:pb-24">
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-14 items-start">
          <div>
            <motion.p {...item(0)} className="vx-eyebrow inline-flex items-center gap-3 text-[hsl(var(--vx-frost))]">
              <span className="relative block w-10 h-px bg-white/20 overflow-hidden"><span className="vx-airflow absolute inset-0" /></span>
              Trusted AC & HVAC specialists
            </motion.p>

            <motion.h1 {...item(0.08)} className="vx-display mt-6 text-[clamp(3rem,8vw,6.5rem)] font-bold">
              Complete climate
              <br />
              <span className="text-[hsl(var(--vx-frost))]">solutions.</span>
            </motion.h1>

            <motion.p {...item(0.16)} className="mt-7 max-w-xl text-lg md:text-xl text-white/70 leading-relaxed">
              Expert air conditioning, ventilation, heating, and HVAC systems for homes and businesses across the UK. From installation to maintenance — one team, total comfort.
            </motion.p>

            <motion.div {...item(0.24)} className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link href="/quote" className="group inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full bg-[hsl(var(--vx-ember))] text-white font-semibold transition-transform hover:-translate-y-0.5 motion-reduce:transform-none" data-testid="button-hero-quote">
                Request free site survey
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none" aria-hidden="true" />
              </Link>
              <button
                type="button"
                onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth" })}
                className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors"
                data-testid="button-hero-services"
              >
                Explore services <ArrowDown className="w-4 h-4" aria-hidden="true" />
              </button>
            </motion.div>

            <motion.div {...item(0.32)} className="mt-12 max-w-2xl">
              <div className="rounded-2xl bg-white/[0.06] ring-1 ring-white/10 p-5 flex flex-wrap items-center justify-between gap-6">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-4xl font-bold">5.0</span>
                  <span className="flex text-[hsl(var(--vx-ember))]" aria-label="Five star rating">
                    {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" aria-hidden="true" />)}
                  </span>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-white/50 text-xs uppercase tracking-wider">Years exp.</dt><dd className="font-display text-2xl font-bold">15+</dd></div>
                  <div><dt className="text-white/50 text-xs uppercase tracking-wider">Support</dt><dd className="font-display text-2xl font-bold">24/7</dd></div>
                </dl>
              </div>
            </motion.div>
          </div>

          <motion.div {...item(0.2)} className="lg:sticky lg:top-24">
            <CallbackForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
